// ============================================================================
// الملف: src/pages/admin/LegalEditor.tsx
// المسؤولية: تحرير سياسة الخصوصية وشروط الاستخدام (للأدمن فقط)
// ============================================================================

import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import Sidebar from '../../components/Layout/Sidebar';
import Header from '../../components/Layout/Header';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import {Shield, FileText, Save, Eye, AlertCircle, CheckCircle} from 'lucide-react';
import {api} from '../../services/api';

interface LegalContent {
    id: number;
    content: string;
    version: string;
    updated_at: string;
}

const LegalEditor: React.FC = () => {
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'privacy' | 'terms'>('privacy');
    const [content, setContent] = useState('');
    const [version, setVersion] = useState('1.0.0');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

    // جلب المحتوى عند تغيير التبويب
    useEffect(() => {
        fetchContent();
    }, [activeTab]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const endpoint = activeTab === 'privacy'
                ? '/api/admin/privacy-policies'
                : '/api/admin/terms-of-service';

            const response = await api.get(endpoint);
            if (response.data.success && response.data.data.length > 0) {
                // خذ أحدث إصدار
                const latest = response.data.data[0];
                setContent(latest.content);
                setVersion(latest.version);
            } else {
                // محتوى افتراضي
                setContent(getDefaultContent(activeTab));
                setVersion('1.0.0');
            }
        } catch (error) {
            console.error('Error fetching legal content:', error);
            setMessage({type: 'error', text: 'فشل تحميل المحتوى'});
        } finally {
            setLoading(false);
        }
    };

    const getDefaultContent = (type: 'privacy' | 'terms'): string => {
        if (type === 'privacy') {
            return `<h1>سياسة الخصوصية</h1>
<p>آخر تحديث: ${new Date().toLocaleDateString('ar-LB')}</p>
<h2>1. المقدمة</h2>
<p>نحن في Transfer SMS نلتزم بحماية خصوصية مستخدمينا.</p>`;
        } else {
            return `<h1>شروط الاستخدام</h1>
<p>آخر تحديث: ${new Date().toLocaleDateString('ar-LB')}</p>
<h2>1. قبول الشروط</h2>
<p>باستخدامك للتطبيق، فإنك توافق على هذه الشروط.</p>`;
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);

        try {
            const endpoint = activeTab === 'privacy'
                ? '/api/admin/privacy-policies'
                : '/api/admin/terms-of-service';

            const response = await api.post(endpoint, {
                content,
                version
            });

            if (response.data.success) {
                setMessage({type: 'success', text: 'تم الحفظ بنجاح'});
                setTimeout(() => setMessage(null), 3000);
            } else {
                setMessage({type: 'error', text: response.data.error || 'فشل الحفظ'});
            }
        } catch (error) {
            console.error('Error saving legal content:', error);
            setMessage({type: 'error', text: 'حدث خطأ في الاتصال'});
        } finally {
            setSaving(false);
        }
    };

    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    return (
        <div className="min-h-screen bg-gray-50">
            <Header toggleSidebar={toggleSidebar} />
            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isMobile={true} />
            <div className="hidden lg:block"><Sidebar isMobile={false} /></div>

            <main className="pt-16 lg:pr-64">
                <div className="p-4 lg:p-6">
                    {/* العنوان */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <FileText className="w-6 h-6 text-blue-600" />
                            <h1 className="text-2xl font-bold text-gray-800">إدارة المحتوى القانوني</h1>
                        </div>
                        <Button
                            variant="primary"
                            icon={<Save className="w-4 h-4" />}
                            onClick={handleSave}
                            loading={saving}
                        >
                            حفظ التغييرات
                        </Button>
                    </div>

                    {/* رسالة النجاح/الخطأ */}
                    {message && (
                        <div className="mb-4 animate-fade-in-down">
                            <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                                }`}>
                                {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                <span>{message.text}</span>
                            </div>
                        </div>
                    )}

                    {/* تبويبات */}
                    <div className="flex gap-2 mb-6 border-b border-gray-200">
                        <button
                            onClick={() => setActiveTab('privacy')}
                            className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors ${activeTab === 'privacy' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <Shield className="w-4 h-4" />
                            سياسة الخصوصية
                        </button>
                        <button
                            onClick={() => setActiveTab('terms')}
                            className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors ${activeTab === 'terms' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <FileText className="w-4 h-4" />
                            شروط الاستخدام
                        </button>
                    </div>

                    {/* رقم الإصدار */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            رقم الإصدار
                        </label>
                        <input
                            type="text"
                            value={version}
                            onChange={(e) => setVersion(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="1.0.0"
                        />
                    </div>

                    {/* محرر النصوص - بسيط */}
                    <Card title="المحتوى">
                        {loading ? (
                            <div className="flex justify-center items-center h-64">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                            </div>
                        ) : (
                            <textarea
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                className="w-full h-96 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                dir="rtl"
                            />
                        )}
                    </Card>

                    {/* زر المعاينة */}
                    <div className="mt-4 flex justify-end gap-2">
                        <Button
                            variant="outline"
                            icon={<Eye className="w-4 h-4" />}
                            onClick={() => window.open(`/legal/${activeTab}`, '_blank')}
                        >
                            معاينة
                        </Button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default LegalEditor;